import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { storage } from 'src/environments/environment';
import { UserRole } from 'src/app/shared/common';

export interface DecodedToken {
  name?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  tenant_id?: string;
  tenant_name?: string;
  tenant_logo?: string;
  user_id?: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class TokenStorageService {
  saveTokens(access: string, refresh?: string) {
    localStorage.setItem(storage.access, access);
    if (refresh) {
      localStorage.setItem(storage.refresh, refresh);
    }
  }

  // Identifies the current login. Stamped fresh on every login (startLoginSession)
  // and cleared on logout (clearTokenStorage / localStorage.clear), but deliberately
  // left untouched by saveTokens, so token rotation keeps the same session id. This
  // lets long-lived, detached work (e.g. a token refresh in flight) tell "the same
  // login rotated its tokens" apart from "the login ended or a different account
  // signed in", which look identical if you only compare the stored tokens.
  startLoginSession() {
    localStorage.setItem(storage.login_session, this.generateSessionId());
  }

  getLoginSession(): string | null {
    return localStorage.getItem(storage.login_session);
  }

  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  getAccessToken(): string | null {
    return localStorage.getItem(storage.access);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(storage.refresh);
  }

  decodeAccessToken(): DecodedToken | null {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      if (decoded && decoded.role && typeof decoded.role === 'string') {
        decoded.role = decoded.role.toLowerCase();
      }
      return decoded;
    } catch {
      return null;
    }
  }

  getCurrentRole(): UserRole {
    const decoded = this.decodeAccessToken();
    const roleFromStorage = localStorage.getItem(storage.role);
    const currentRole = decoded?.role || roleFromStorage;
    return (currentRole ? currentRole.toLowerCase() : UserRole.USER) as UserRole;
  }

  clearTokenStorage() {
    localStorage.clear();
  }
}
